/**
 * HRDashboard — Vue d'ensemble RH (parité BE-TAYE HRDashboardPage.jsx).
 * KPI Framer Motion + widgets Anniversaires & Périodes d'essai.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Users, UserCheck, UserX, ArrowRight, Briefcase, Cake, AlarmClock,
  Calendar as CalIcon, Wallet, Sun, ShieldAlert, AlertTriangle,
  FileClock, Clock, IdCard, Stethoscope, MessageSquare, MessageSquareWarning,
  Award, ChevronRight, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

const ALERT_ICONS = {
  cdd_end_soon: FileClock,
  probation_end_soon: Clock,
  residence_permit_expiring: IdCard,
  medical_visit_due: Stethoscope,
  interview_pro_due: MessageSquare,
  interview_pro_overdue: MessageSquareWarning,
  anniversary_milestone: Award,
};

const COLOR_CLASSES = {
  blue:    "bg-blue-100 text-blue-700",
  emerald: "bg-emerald-100 text-emerald-700",
  amber:   "bg-amber-100 text-amber-700",
  slate:   "bg-slate-200 text-slate-700",
};

const KPI = ({ label, value, icon: Icon, color = "blue", idx = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.05 * idx, duration: 0.3 }}
    data-testid={`hr-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <Card>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${COLOR_CLASSES[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
          <p className="text-2xl font-bold text-slate-900 font-mono">{value}</p>
        </div>
      </CardContent>
    </Card>
  </motion.div>
);

const MONTH_LABEL = ["janvier", "février", "mars", "avril", "mai", "juin",
                       "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

export default function HRDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, active: 0, on_leave: 0, terminated: 0 });
  const [alerts, setAlerts] = useState({ birthdays_this_month: [], probation_ending_soon: [] });
  const [pendingAdv, setPendingAdv] = useState([]);
  const [lowBalances, setLowBalances] = useState([]);
  const [complianceAlerts, setComplianceAlerts] = useState([]);
  const [complianceCounts, setComplianceCounts] = useState({ critical: 0, warning: 0, info: 0, total: 0 });
  useEffect(() => { (async () => {
    try {
      const [s, a, adv, bal, comp, compC] = await Promise.all([
        api.get("/hr/stats"), api.get("/hr/alerts"),
        api.get("/hr/advances", { params: { status: "pending" } }),
        api.get("/hr/leave-balances"),
        api.get("/hr/compliance/alerts", { params: { severity: "critical", horizon_days: 90 } }),
        api.get("/hr/compliance/alerts/counts"),
      ]);
      setStats(s.data); setAlerts(a.data);
      setPendingAdv(adv.data.advances || []);
      setLowBalances((bal.data.balances || []).filter(b => b.balance < 5).slice(0, 5));
      setComplianceAlerts(comp.data.alerts || []);
      setComplianceCounts(compC.data.counts || { critical: 0, warning: 0, info: 0, total: 0 });
    } catch (e) { toast.error(apiError(e)); }
  })(); }, []);

  const criticalAlerts = useMemo(
    () => (complianceAlerts || []).slice().sort((x, y) => {
      const dx = x.days_until ?? 9999;
      const dy = y.days_until ?? 9999;
      return dx - dy;
    }),
    [complianceAlerts]
  );

  const currentMonth = MONTH_LABEL[new Date().getMonth()];

  return (
    <div className="space-y-6" data-testid="hr-dashboard">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
          Ressources Humaines
        </h1>
        <p className="text-sm text-slate-500">Vue d'ensemble de vos équipes</p>
      </div>

      {/* Widget Alertes critiques compliance RH */}
      <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
                   transition={{ duration: 0.35 }}
                   data-testid="widget-compliance-alerts">
        <Card className={criticalAlerts.length > 0
            ? "border-red-200 bg-gradient-to-br from-red-50/60 via-white to-white"
            : "border-emerald-200 bg-gradient-to-br from-emerald-50/50 via-white to-white"}>
          <CardHeader className="pb-2 flex flex-row items-center gap-2">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
              criticalAlerts.length > 0
                ? "bg-red-100 text-red-700"
                : "bg-emerald-100 text-emerald-700"
            }`}>
              {criticalAlerts.length > 0
                ? <ShieldAlert className="w-5 h-5" />
                : <ShieldCheck className="w-5 h-5" />}
            </div>
            <div className="flex-1">
              <CardTitle className="text-base flex items-center gap-2">
                Alertes conformité RH critiques
                {criticalAlerts.length > 0 && (
                  <Badge className="bg-red-100 text-red-700 border-red-200" variant="outline">
                    {criticalAlerts.length}
                  </Badge>
                )}
              </CardTitle>
              <p className="text-xs text-slate-500 mt-0.5">
                Fin CDD · Essai · Visite médicale · Titre séjour · Entretien pro — à traiter sous 7 jours
              </p>
            </div>
            <div className="hidden sm:flex items-center gap-1.5">
              {complianceCounts.warning > 0 && (
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">
                  {complianceCounts.warning} à surveiller
                </Badge>
              )}
              {complianceCounts.info > 0 && (
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[10px]">
                  {complianceCounts.info} à anticiper
                </Badge>
              )}
            </div>
            <Button size="sm" variant="outline" onClick={() => navigate("/app/hr/alerts")}
              data-testid="widget-compliance-view-all"
              className="ml-1 shrink-0">
              Voir tout <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {criticalAlerts.length === 0 ? (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-50/60 border border-emerald-200/60"
                data-testid="compliance-empty">
                <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-emerald-800">Tout est à jour</p>
                  <p className="text-xs text-emerald-700/80">
                    Aucune échéance critique dans les 7 prochains jours.
                    {complianceCounts.warning + complianceCounts.info > 0 &&
                      ` ${complianceCounts.warning + complianceCounts.info} alerte(s) non critique(s) à consulter.`}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {criticalAlerts.map((a, i) => {
                  const Icon = ALERT_ICONS[a.type] || AlertTriangle;
                  const isOverdue = (a.days_until ?? 0) < 0;
                  return (
                    <button
                      key={a.id}
                      onClick={() => navigate(`/app/hr/employees/${a.employee_id}`)}
                      className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white/70 hover:bg-red-50 border border-red-100/60 hover:border-red-200 transition text-left"
                      data-testid={`widget-alert-${i}`}
                    >
                      <div className="w-9 h-9 rounded-full bg-red-100 text-red-700 flex items-center justify-center shrink-0">
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-slate-900 text-sm truncate">
                            {a.employee_name}
                          </span>
                          <Badge variant="outline"
                            className="bg-red-50 text-red-700 border-red-200 text-[10px]">
                            {a.type_label}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-600 mt-0.5 truncate">
                          {a.detail}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <Badge className={isOverdue
                            ? "bg-red-600 text-white border-red-700"
                            : "bg-red-100 text-red-700 border-red-200"}
                          variant="outline">
                          {a.days_label}
                        </Badge>
                        {a.target_date && (
                          <p className="text-[10px] text-slate-400 mt-0.5 font-mono">
                            {new Date(a.target_date).toLocaleDateString("fr-FR")}
                          </p>
                        )}
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI label="Effectif total" value={stats.total} icon={Users} color="blue" idx={0} />
        <KPI label="Actifs" value={stats.active} icon={UserCheck} color="emerald" idx={1} />
        <KPI label="En congé" value={stats.on_leave} icon={Briefcase} color="amber" idx={2} />
        <KPI label="Sortis" value={stats.terminated} icon={UserX} color="slate" idx={3} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                     transition={{ delay: 0.25, duration: 0.3 }}>
          <Card data-testid="widget-birthdays">
            <CardHeader className="pb-2 flex flex-row items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-pink-100 text-pink-700 flex items-center justify-center">
                <Cake className="w-4 h-4" />
              </div>
              <CardTitle className="text-base">Anniversaires de {currentMonth}</CardTitle>
              <Badge variant="outline" className="ml-auto text-[10px]">
                {alerts.birthdays_this_month.length}
              </Badge>
            </CardHeader>
            <CardContent>
              {alerts.birthdays_this_month.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">Aucun anniversaire ce mois-ci</p>
              ) : (
                <div className="space-y-2">
                  {alerts.birthdays_this_month.map((b) => (
                    <button key={b.id} onClick={() => navigate(`/app/hr/employees/${b.id}`)}
                      className="w-full flex items-center justify-between p-2.5 rounded-lg bg-pink-50/40 hover:bg-pink-50 transition text-left"
                      data-testid={`birthday-${b.id}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-pink-100 text-pink-700 flex items-center justify-center text-xs font-bold">
                          {b.day}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 text-sm">{b.first_name} {b.last_name}</p>
                          <p className="text-xs text-slate-500">{b.job_title || "—"}</p>
                        </div>
                      </div>
                      <Badge variant="outline" className="bg-white text-slate-600 text-xs">
                        {b.age_turning} ans
                      </Badge>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                     transition={{ delay: 0.3, duration: 0.3 }}>
          <Card data-testid="widget-probation">
            <CardHeader className="pb-2 flex flex-row items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center">
                <AlarmClock className="w-4 h-4" />
              </div>
              <CardTitle className="text-base">Fins de période d'essai (15 j)</CardTitle>
              <Badge variant="outline" className="ml-auto text-[10px]">
                {alerts.probation_ending_soon.length}
              </Badge>
            </CardHeader>
            <CardContent>
              {alerts.probation_ending_soon.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">Aucune période d'essai à surveiller</p>
              ) : (
                <div className="space-y-2">
                  {alerts.probation_ending_soon.map((p, i) => (
                    <button key={i} onClick={() => navigate(`/app/hr/employees/${p.employee_id}`)}
                      className="w-full flex items-center justify-between p-2.5 rounded-lg bg-amber-50/40 hover:bg-amber-50 transition text-left"
                      data-testid={`probation-${p.employee_id}`}>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{p.first_name} {p.last_name}</p>
                        <p className="text-xs text-slate-500">
                          {p.job_title || "—"} · {p.contract_type?.toUpperCase()}
                        </p>
                      </div>
                      <div className="text-right">
                        <Badge className={`text-[11px] ${p.days_left <= 3 ? "bg-red-100 text-red-700 border-red-200" : "bg-amber-100 text-amber-700 border-amber-200"}`}
                          variant="outline">
                          J-{p.days_left}
                        </Badge>
                        <p className="text-[10px] text-slate-400 mt-0.5 font-mono">
                          {new Date(p.probation_end_date).toLocaleDateString("fr-FR")}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.3 }}>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Accès rapides</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <button onClick={() => navigate("/app/hr/salaries")}
              className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-blue-50 transition text-left"
              data-testid="hr-quick-salaries">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center"><Users className="w-5 h-5" /></div>
                <div>
                  <p className="font-medium text-slate-900">Salariés</p>
                  <p className="text-xs text-slate-500">Créer, modifier, filtrer, exporter</p>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400" />
            </button>
            <button onClick={() => navigate("/app/hr/absences")}
              className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-blue-50 transition text-left"
              data-testid="hr-quick-absences">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-violet-100 text-violet-700 flex items-center justify-center"><CalIcon className="w-5 h-5" /></div>
                <div>
                  <p className="font-medium text-slate-900">Absences & congés</p>
                  <p className="text-xs text-slate-500">Valider CP / RTT / arrêts</p>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400" />
            </button>
            <button onClick={() => navigate("/app/hr/advances")}
              className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-blue-50 transition text-left"
              data-testid="hr-quick-advances">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center"><Wallet className="w-5 h-5" /></div>
                <div>
                  <p className="font-medium text-slate-900">Acomptes</p>
                  <p className="text-xs text-slate-500">Valider, payer, injecter en paie</p>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400" />
            </button>
          </CardContent>
        </Card>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.3 }}>
          <Card data-testid="widget-pending-advances">
            <CardHeader className="pb-2 flex flex-row items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
                <Wallet className="w-4 h-4" />
              </div>
              <CardTitle className="text-base">Acomptes en attente</CardTitle>
              <Badge variant="outline" className="ml-auto text-[10px]">{pendingAdv.length}</Badge>
            </CardHeader>
            <CardContent>
              {pendingAdv.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">Aucune demande à valider</p>
              ) : (
                <div className="space-y-2">
                  {pendingAdv.slice(0, 4).map((a) => (
                    <button key={a.id} onClick={() => navigate("/app/hr/advances")}
                      className="w-full flex items-center justify-between p-2.5 rounded-lg bg-emerald-50/40 hover:bg-emerald-50 transition text-left"
                      data-testid={`pending-adv-${a.id}`}>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{a.employee_name}</p>
                        <p className="text-xs text-slate-500 truncate max-w-[220px]">{a.reason || "—"}</p>
                      </div>
                      <Badge variant="outline" className="bg-white text-emerald-700 border-emerald-200 font-mono">
                        {Number(a.amount).toLocaleString("fr-FR", { minimumFractionDigits: 0 })} €
                      </Badge>
                    </button>
                  ))}
                  {pendingAdv.length > 4 && (
                    <button onClick={() => navigate("/app/hr/advances")}
                      className="w-full text-xs text-emerald-700 hover:underline pt-1 text-center">
                      + {pendingAdv.length - 4} autres →
                    </button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 0.3 }}>
          <Card data-testid="widget-low-balances">
            <CardHeader className="pb-2 flex flex-row items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center">
                <Sun className="w-4 h-4" />
              </div>
              <CardTitle className="text-base">Soldes CP faibles (&lt; 5 j)</CardTitle>
              <Badge variant="outline" className="ml-auto text-[10px]">{lowBalances.length}</Badge>
            </CardHeader>
            <CardContent>
              {lowBalances.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">Tous les salariés ont plus de 5 jours restants</p>
              ) : (
                <div className="space-y-2">
                  {lowBalances.map((b) => (
                    <button key={b.employee_id} onClick={() => navigate(`/app/hr/employees/${b.employee_id}`)}
                      className="w-full flex items-center justify-between p-2.5 rounded-lg bg-blue-50/40 hover:bg-blue-50 transition text-left"
                      data-testid={`low-bal-${b.employee_id}`}>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{b.first_name} {b.last_name}</p>
                        <p className="text-xs text-slate-500">{b.job_title || "—"} · Pris {b.taken}/{b.acquired} j</p>
                      </div>
                      <Badge variant="outline"
                        className={`font-mono ${b.balance <= 0 ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                        {b.balance} j
                      </Badge>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
